import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

type ToastVariant = "info" | "success" | "danger";

interface ToastContextValue {
  show: (text: string, variant?: ToastVariant, durationMs?: number) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [text, setText] = useState<string>("");
  const [variant, setVariant] = useState<ToastVariant>("info");
  const [visible, setVisible] = useState(false);
  const timer = useRef<number | null>(null);

  const show = useCallback(
    (msg: string, v: ToastVariant = "info", durationMs = 2000) => {
      setText(msg);
      setVariant(v);
      setVisible(true);
      if (timer.current) clearTimeout(timer.current);
      timer.current = window.setTimeout(() => setVisible(false), durationMs);
    },
    [],
  );

  const value = useMemo(() => ({ show }), [show]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {/* The legacy CSS in static/css/styles.css styles `.toast.show`, so we
          reuse those class names rather than introducing parallel styles. */}
      <div
        className={`toast ${variant} ${visible ? "show" : ""}`}
        role="status"
        aria-live="polite"
      >
        {text}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used inside <ToastProvider>");
  return ctx;
}
